import type {
  AccountBalance,
  Book,
  Category,
  Currency,
  DashboardData,
  JournalEntry,
  LedgerAccount,
  NetWorth,
  MoneyFlowReport,
  SpendingReport,
  UserPreferences,
  WorkspaceManifest,
  WorkspaceIDs,
} from "../../entities/ledger/types";
import { ApiError, apiRequest } from "../../shared/api/client";

export function monthWindow(date = new Date()): WorkspaceIDs["period"] {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  return {
    key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`,
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

async function tolerateConflict(operation: () => Promise<unknown>): Promise<void> {
  try {
    await operation();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
  }
}

export function getPreferences(): Promise<UserPreferences> {
  return apiRequest("/v1/preferences");
}

export async function ensureWorkspace(currency: Currency, limit: string, createUser: boolean): Promise<WorkspaceIDs> {
  const period = monthWindow();
  if (createUser) {
    await tolerateConflict(() => apiRequest("/v1/users", {
      method: "POST",
      body: { defaultCurrency: currency },
    }));
  }
  const workspace = createUser
    ? await apiRequest<{ manifest: WorkspaceManifest }>("/v1/workspace", { method: "POST" })
    : await apiRequest<{ manifest: WorkspaceManifest }>("/v1/me/workspace");
  const manifest = workspace.manifest;
  const baseCurrency = manifest.book.baseCurrency;
  // The manifest's accounts are only a starting selection: the server resolves
  // the real expense, income and fee accounts per currency when posting. A
  // manifest that names none of them still gives a usable workspace.
  const seeded = manifest.accounts ?? {};
  const ids: WorkspaceIDs = {
    user: manifest.book.ownerId,
    book: manifest.book.id,
    baseCurrency,
    cash: seeded.primary_asset?.id ?? "",
    expense: seeded.default_expense?.id ?? "",
    income: seeded.default_income?.id ?? "",
    fees: seeded.fee_expense?.id ?? "",
    period,
  };

  // A fresh workspace starts with one budget for this month covering every
  // category. Budgets are managed afterwards on the planning page.
  if (createUser && limit !== "") {
    const query = new URLSearchParams({ from: period.start, to: period.end });
    const existing = await apiRequest<{ budgets: { budget: { id: string } }[] }>(
      `/v1/books/${encodeURIComponent(ids.book)}/planning/budget-progress?${query}`);
    if (existing.budgets.length === 0) {
      await apiRequest(`/v1/books/${encodeURIComponent(ids.book)}/planning/budgets`, {
        method: "POST",
        body: {
          name: "Monthly spending", limit: { amount: limit, currency: baseCurrency },
          categoryIds: [], forecastMode: "remaining", periodStart: period.start, periodEnd: period.end,
        },
      });
    }
  }
  return ids;
}

// activeBookID overrides the workspace book so an owner with several books
// reads the one they switched to; the default accounts of the manifest are
// only used as a starting selection, never sent to the API.
export async function loadDashboard(currency: Currency, activeBookID = ""): Promise<DashboardData> {
  const workspace = await ensureWorkspace(currency, "", false);
  const ids = activeBookID && activeBookID !== workspace.book ? { ...workspace, book: activeBookID } : workspace;
  const query = new URLSearchParams({ from: ids.period.start, to: ids.period.end, limit: "100" });
  const reportQuery = new URLSearchParams({ from: ids.period.start, to: ids.period.end });
  const [accountPage, balances, entryPage, book, categoryPage, report] = await Promise.all([
    apiRequest<{ accounts: LedgerAccount[] }>(`/v1/books/${encodeURIComponent(ids.book)}/accounts`),
    apiRequest<{ balances: AccountBalance[]; netWorth: NetWorth }>(`/v1/books/${encodeURIComponent(ids.book)}/account-balances`),
    apiRequest<{ transactions: JournalEntry[] }>(`/v1/books/${encodeURIComponent(ids.book)}/transactions?${query}`),
    apiRequest<Book>(`/v1/books/${encodeURIComponent(ids.book)}`),
    apiRequest<{ categories: Category[] }>(`/v1/books/${encodeURIComponent(ids.book)}/categories`),
    apiRequest<SpendingReport>(`/v1/books/${encodeURIComponent(ids.book)}/reports/spending?${reportQuery}`),
  ]);
  const assetIDs = accountPage.accounts.map((account) => account.id);
  let moneyFlow: MoneyFlowReport | null = null;
  if (assetIDs.length > 0) {
    const flowQuery = new URLSearchParams({ from: ids.period.start, to: ids.period.end });
    assetIDs.forEach((accountID) => flowQuery.append("accountId", accountID));
    moneyFlow = await apiRequest<MoneyFlowReport>(`/v1/books/${encodeURIComponent(ids.book)}/money-flow?${flowQuery}`);
  }
  return {
    ids: { ...ids, baseCurrency: book.baseCurrency }, book, accounts: accountPage.accounts, balances: balances.balances, netWorth: balances.netWorth, entries: entryPage.transactions, moneyFlow,
    categories: categoryPage.categories, report,
  };
}

export function setReportingCurrency(bookID: string, currency: Currency): Promise<Book> {
  return apiRequest(`/v1/books/${encodeURIComponent(bookID)}/reporting-currency`, { method: "PUT", body: { reportingCurrency: currency } });
}
